import { useMemo } from "react";
import * as Yup from "yup";

import {
  ADDRESS_LOCATION_KEYS,
  ADDRESS_RESET_ON_COUNTRY_CHANGE_FIELDS,
  ADDRESS_VALIDATION_MESSAGES,
  CONTACT_FIELD_KEYS,
  CONTACT_PHONE_DIGITS_PATTERN,
  CONTACT_VALIDATION_MESSAGES,
  SELECT_OPTION_FALLBACK_LABEL,
  SELECT_OPTION_ID_PATHS,
  SELECT_OPTION_LABEL_PATHS,
  EMPTY_ADDRESS,
  EMPTY_CONTACT,
  GSTIN_PATTERN,
  INDIA_STATE_DISTRICT_FORM_FIELDS,
  ISD_CODE_PHONE_LENGTH,
  ISD_CODE_PREFIX,
  ISD_CODE_WITH_PLUS_PATTERN,
  EMAIL_PATTERN,
  MAX_TAX_REGISTRATION_NUMBER_LENGTH,
  MIN_REQUIRED_SELECTION_COUNT,
  NON_DIGIT_PATTERN,
  OBJECT_PATH_SEPARATOR,
  TENANT_CODE_PATTERN,
  TENANT_FORM_FIELDS,
  TENANT_VALIDATION_MESSAGES,
  TENANT_YUP_TEST_NAMES,
  UUID_PATTERN,
  YUP_REQUIRED_TEST_SUFFIX,
  ZERO,
} from "constants/constants";
import {
  GRADE_DISPLAY_PREFIX,
  INDIA_POSTAL_CODE_LENGTH,
  INDIA_POSTAL_CODE_PATTERN,
  VIEW_DASH,
} from "constants/schoolConstants";
import {
  findMappingItemById,
  idsMatch,
  isIndiaCountry,
  isIndiaCountryId,
} from "helpers/schoolUtils";
import {
  useFetchCountryList,
  useFetchGradeList,
  useFetchPostalCodeDetails,
  useFetchVerticalList,
} from "store/backend/apiHooks";

const isValidFormId = (value) =>
  value != null && value !== "" && !Number.isNaN(Number(value));

const hasTrimmedText = (value) => !!String(value || "").trim();

const getPrimitiveValue = (value) =>
  value != null && typeof value !== "object" ? value : undefined;

const getValueAtPath = (obj, path) =>
  path.split(OBJECT_PATH_SEPARATOR).reduce((acc, key) => acc?.[key], obj);

const getFirstPrimitiveAlongPaths = (item, paths) => {
  for (const path of paths) {
    const primitiveValue = getPrimitiveValue(getValueAtPath(item, path));
    if (primitiveValue != null) return primitiveValue;
  }
  return undefined;
};

const setMultipleFieldValues = (setFieldValue, fields, value = "", validate = false) => {
  fields.forEach((field) => setFieldValue(field, value, validate));
};

const toNullableString = (value) => (value?.trim() ? value : null);

const requiredIdSchema = (requiredMsg, selectMsg) =>
  Yup.mixed()
    .required(requiredMsg)
    .test(TENANT_YUP_TEST_NAMES.ID_SELECTED, selectMsg, isValidFormId);

const createOptionalEmailSchema = () =>
  Yup.string()
    .email(TENANT_VALIDATION_MESSAGES.EMAIL_INVALID)
    .nullable()
    .notRequired()
    .test(
      TENANT_YUP_TEST_NAMES.AMBASSADOR_EMAIL,
      TENANT_VALIDATION_MESSAGES.EMAIL_INVALID,
      (value) => (!value ? true : EMAIL_PATTERN.test(value)),
    );

const normalizeContactFields = (contact) =>
  contact
    ? { ...contact, isdCode: contact.isdCode ?? contact.isd_code ?? "" }
    : { ...EMPTY_CONTACT };

const resolveIsdCodeDisplayLabel = (isdCode, options, isLoading) => ({
  value:
    findOptionById(options, isdCode)?.label ??
    (isdCode ? String(isdCode) : null),
  isLoading,
});

export const formatDisplayValue = (value, empty = VIEW_DASH) => {
  if (value == null) return empty;
  if (typeof value === "string" && !value.trim()) return empty;
  return value;
};

export const formatGradeLabel = (nameOrId) =>
  `${GRADE_DISPLAY_PREFIX}${nameOrId}`;

export const mapGradesToOptions = (grades = []) =>
  grades.map((grade) => ({
    value: grade.id,
    label: formatGradeLabel(grade.name),
  }));

export const mapVerticalsToOptions = (verticals = []) =>
  verticals.map((vertical) => ({
    value: vertical.id,
    label: vertical.name,
  }));

export const getAddressFormFlagsForCountry = (selectedCountry) => {
  const isIndia = isIndiaCountry(selectedCountry?.label);
  return {
    isIndia,
    isAreaRequired: !!selectedCountry && !isIndia,
    isAreaDisabled: !selectedCountry || isIndia,
    isStateDistrictReadOnly: !!selectedCountry,
    showTaxRegistrationNumber: !!selectedCountry && !isIndia,
    isCountryDerivedFieldDisabled: !selectedCountry,
  };
};

export const normalizePinCodeInput = (value, isIndia) => {
  const nextValue = String(value ?? "");
  return isIndia
    ? nextValue.replace(NON_DIGIT_PATTERN, "").slice(ZERO, INDIA_POSTAL_CODE_LENGTH)
    : nextValue;
};

export const clearStateAndDistrictWhenPinIncomplete = (
  setFieldValue,
  isIndia,
  pinCode,
) => {
  if (!isIndia) return;
  const pinCodeLength = String(pinCode || "").length;
  if (pinCodeLength > ZERO && pinCodeLength < INDIA_POSTAL_CODE_LENGTH) {
    setMultipleFieldValues(setFieldValue, INDIA_STATE_DISTRICT_FORM_FIELDS);
  }
};

export const fillAddressFromPostalCode = (
  setFieldValue,
  postalCodeDetails,
) => {
  if (!postalCodeDetails?.data?.district) return;
  const { district, state } = postalCodeDetails.data;
  setFieldValue(TENANT_FORM_FIELDS.ADDRESS_STATE_ID, Number(state.id), false);
  setFieldValue(TENANT_FORM_FIELDS.ADDRESS_STATE, state.name, false);
  setFieldValue(TENANT_FORM_FIELDS.ADDRESS_DISTRICT_ID, district.id, false);
  setFieldValue(TENANT_FORM_FIELDS.ADDRESS_DISTRICT, district.name, false);
};

export const resetIncompatibleTaxFieldsForCountry = (values, { isIndia } = {}) => {
  if (isIndia) return { ...values, taxRegistrationNumber: "" };
  if (isIndia === false) return { ...values, gstin: "" };
  return values;
};

const getFormRootCountryId = (testContext) =>
  testContext.from?.[testContext.from.length - 1]?.value?.countryId;

const resolveOptionId = (item) =>
  getFirstPrimitiveAlongPaths(item, SELECT_OPTION_ID_PATHS) ?? getPrimitiveValue(item);

const resolveOptionLabel = (item, fallbackPrefix) => {
  const value = resolveOptionId(item);
  return (
    getFirstPrimitiveAlongPaths(item, SELECT_OPTION_LABEL_PATHS) ??
    (value ? `${fallbackPrefix} ${value}` : "")
  );
};

export const mapToSelectOptions = (items = [], fallbackPrefix) =>
  items
    .map((item) => {
      const value = resolveOptionId(item);
      if (value == null || value === "") return null;
      return { key: value, value, label: resolveOptionLabel(item, fallbackPrefix) };
    })
    .filter(Boolean);

export const findOptionById = (options, value) =>
  options.find((option) => String(option.value) === String(value));

export const getCountryOptions = (countryList) =>
  countryList.map((country) => ({
    key: country.id,
    value: country.id,
    label: country.displayName || country.name,
    currencies: country.currencies || [],
    timezones: country.timezones || [],
    isdCodes: country.isdCodes || [],
  }));

export const TENANT_FORM_INITIAL_VALUES = {
  name: "",
  verticalId: "",
  code: "",
  logo: "",
  email: "",
  profileSeparate: false,
  hasSingleBranch: false,
  gstin: null,
  taxRegistrationNumber: "",
  countryId: "",
  timezoneIds: [],
  currencyIds: [],
  ambassadorMail: "",
  gradeIds: [],
  address: { ...EMPTY_ADDRESS },
  contact: { ...EMPTY_CONTACT },
};

const normalizeToIdList = (...candidates) => {
  const value = candidates.find(
    (candidate) =>
      candidate != null &&
      candidate !== "" &&
      (!Array.isArray(candidate) || candidate.length > ZERO),
  );
  if (Array.isArray(value)) return value;
  return value != null && value !== "" ? [value] : [];
};

export const normalizeAddressFields = (address = {}) => ({
  addressLine1: address.addressLine1 || address.address_line_1 || "",
  addressLine2: address.addressLine2 || address.address_line_2 || "",
  street: address.street || "",
  city: address.city || "",
  district: address.district || "",
  districtId: address.districtId ?? address.district_id ?? "",
  state: address.state || "",
  stateId: address.stateId ?? address.state_id ?? "",
  area: address.area || "",
  pinCode: address.pinCode || address.pin_code || "",
});

const toTrimmedOrNull = (value) => (value?.trim() ? value.trim() : null);

const toRequiredTrimmedString = (value) => String(value ?? "").trim();

export const buildAddressPayload = (address, countryOptions, countryId) => {
  const isIndia = isIndiaCountryId(countryOptions, countryId);
  return {
    address_line_1: address.addressLine1,
    address_line_2: address.addressLine2?.trim() ? address.addressLine2 : "",
    street: address.street?.trim() ? address.street : "",
    city: address.city,
    district_id:
      isIndia && address.districtId ? Number(address.districtId) : null,
    district: isIndia ? toTrimmedOrNull(address.district) : null,
    area: !isIndia ? toTrimmedOrNull(address.area) : null,
    state_id: isIndia && address.stateId ? Number(address.stateId) : null,
    pin_code: toRequiredTrimmedString(address.pinCode),
  };
};

const resolveIsdCodeForPayload = (isdCode, isdCodeOptions = []) => {
  if (isdCode == null || isdCode === "") return null;
  const matchedOption = findOptionById(isdCodeOptions, isdCode);
  return (matchedOption?.label ?? String(isdCode).trim()) || null;
};

export const getIsdCodeOptionsForCountry = (countryOptions, countryId) =>
  mapToSelectOptions(
    findOptionById(countryOptions, countryId)?.isdCodes,
    SELECT_OPTION_FALLBACK_LABEL.ISD,
  );

const extractNormalizedIsdCode = (value) => {
  if (value == null || value === "") return null;
  const text = String(value).trim();
  const withPlusMatch = text.match(ISD_CODE_WITH_PLUS_PATTERN);
  if (withPlusMatch) return withPlusMatch[ZERO];
  if (CONTACT_PHONE_DIGITS_PATTERN.test(text)) {
    return `${ISD_CODE_PREFIX}${text}`;
  }
  return null;
};

const getExpectedPhoneLengthForIsd = (isdCodeValue) => {
  const key = extractNormalizedIsdCode(isdCodeValue);
  return key ? (ISD_CODE_PHONE_LENGTH[key] ?? null) : null;
};

const getContactPairErrorPath = (error) => {
  if (!error) return null;
  if (error.isdCode) return CONTACT_FIELD_KEYS.ISD_CODE;
  if (error.phone) return CONTACT_FIELD_KEYS.PHONE;
  return null;
};

const validatePhoneAndIsdPair = (
  isdCode,
  phone,
  isdCodeOptions = [],
) => {
  const normalizedPhone = toTrimmedOrNull(phone);
  const resolvedIsdCode = resolveIsdCodeForPayload(isdCode, isdCodeOptions);

  if (!normalizedPhone && !resolvedIsdCode) return null;
  if (normalizedPhone && !resolvedIsdCode) {
    return { isdCode: CONTACT_VALIDATION_MESSAGES.ISD_REQUIRED_WITH_PHONE };
  }
  if (resolvedIsdCode && !normalizedPhone) {
    return { phone: CONTACT_VALIDATION_MESSAGES.PHONE_REQUIRED_WITH_ISD };
  }

  const expectedLength = getExpectedPhoneLengthForIsd(resolvedIsdCode);
  if (expectedLength == null) {
    return { isdCode: CONTACT_VALIDATION_MESSAGES.INVALID_ISD };
  }
  if (!CONTACT_PHONE_DIGITS_PATTERN.test(normalizedPhone)) {
    return { phone: CONTACT_VALIDATION_MESSAGES.INVALID_PHONE };
  }
  if (normalizedPhone.length !== expectedLength) {
    return { phone: CONTACT_VALIDATION_MESSAGES.PHONE_LENGTH(expectedLength) };
  }
  return null;
};

export const updateContactPhoneFromInput = (
  event,
  setFieldValue,
  setFieldTouched,
) => {
  if (!event || !event.target) return;

  const newValue = String(event.target.value).replace(NON_DIGIT_PATTERN, "");
  setFieldValue(TENANT_FORM_FIELDS.CONTACT_PHONE, newValue, true);
  setFieldTouched(TENANT_FORM_FIELDS.CONTACT_PHONE, true, false);
};

export const normalizeUuid = (value) => {
  if (value == null || value === "") return null;
  const normalized = String(value).trim();
  return UUID_PATTERN.test(normalized) ? normalized.toLowerCase() : normalized;
};

export const resolveTenantUuid = (tenantDetails, fallbackId = null) =>
  normalizeUuid(tenantDetails?.id ?? tenantDetails?.tenantId ?? fallbackId);

export const resolveBranchUuid = (branchDetails, fallbackId = null) =>
  normalizeUuid(branchDetails?.id ?? branchDetails?.branchId ?? fallbackId);

export const buildContactPayload = (contact, isdCodeOptions = []) => ({
  name: contact.name,
  designation: contact.designation,
  phone: contact.phone,
  isd_code: resolveIsdCodeForPayload(contact.isdCode, isdCodeOptions),
});

export const createGstinSchema = () =>
  Yup.string()
    .nullable()
    .notRequired()
    .test(
      TENANT_YUP_TEST_NAMES.GSTIN,
      TENANT_VALIDATION_MESSAGES.GSTIN_INVALID,
      (value) => (!value ? true : GSTIN_PATTERN.test(value)),
    );

export const createTaxRegistrationNumberSchema = () =>
  Yup.string()
    .nullable()
    .notRequired()
    .max(
      MAX_TAX_REGISTRATION_NUMBER_LENGTH,
      TENANT_VALIDATION_MESSAGES.TAX_REGISTRATION_MAX_LENGTH(
        MAX_TAX_REGISTRATION_NUMBER_LENGTH,
      ),
    );

export const createContactSchema = (
  countryOptions = [],
  getCountryId = () => null,
) =>
  Yup.object()
    .shape({
      name: Yup.string().nullable(),
      designation: Yup.string().nullable(),
      phone: Yup.string().nullable(),
      isdCode: Yup.mixed().nullable(),
    })
    .test(
      TENANT_YUP_TEST_NAMES.CONTACT_PHONE_ISD,
      TENANT_VALIDATION_MESSAGES.CONTACT_INVALID,
      function (contact) {
        if (!contact) return true;

        const error = validatePhoneAndIsdPair(
          contact.isdCode,
          contact.phone,
          getIsdCodeOptionsForCountry(countryOptions, getCountryId(this)),
        );
        if (!error) return true;

        const fieldKey = getContactPairErrorPath(error);
        if (!fieldKey) return false;
        if (!this.path) return false;

        return this.createError({
          path: `${this.path}${OBJECT_PATH_SEPARATOR}${fieldKey}`,
          message: error[fieldKey],
        });
      },
    );

const createIndiaRequiredLocationTests = (
  getCountryId,
  isIndia,
  message,
  nameKey,
  idKey,
) => ({
  [nameKey]: Yup.string().test(
    `${nameKey}${YUP_REQUIRED_TEST_SUFFIX}`,
    message,
    function (value) {
      const countryId = getCountryId(this);
      if (!countryId || !isIndia(countryId)) return true;
      return hasTrimmedText(value) || isValidFormId(this.parent[idKey]);
    },
  ),
  [idKey]: Yup.mixed().test(
    `${idKey}${YUP_REQUIRED_TEST_SUFFIX}`,
    message,
    function (value) {
      const countryId = getCountryId(this);
      if (!countryId || !isIndia(countryId)) return true;
      return isValidFormId(value) || hasTrimmedText(this.parent[nameKey]);
    },
  ),
});

export const createAddressSchema = (countryOptions, getCountryId) => {
  const isIndia = (countryId) => isIndiaCountryId(countryOptions, countryId);

  return {
    addressLine1: Yup.string().required(
      ADDRESS_VALIDATION_MESSAGES.LINE_1_REQUIRED,
    ),
    addressLine2: Yup.string(),
    street: Yup.string(),
    city: Yup.string().required(ADDRESS_VALIDATION_MESSAGES.CITY_REQUIRED),
    ...createIndiaRequiredLocationTests(
      getCountryId,
      isIndia,
      ADDRESS_VALIDATION_MESSAGES.DISTRICT_REQUIRED,
      ADDRESS_LOCATION_KEYS.DISTRICT,
      ADDRESS_LOCATION_KEYS.DISTRICT_ID,
    ),
    ...createIndiaRequiredLocationTests(
      getCountryId,
      isIndia,
      ADDRESS_VALIDATION_MESSAGES.STATE_REQUIRED,
      ADDRESS_LOCATION_KEYS.STATE,
      ADDRESS_LOCATION_KEYS.STATE_ID,
    ),
    area: Yup.string().test(
      TENANT_YUP_TEST_NAMES.AREA_REQUIRED,
      ADDRESS_VALIDATION_MESSAGES.AREA_REQUIRED,
      function (value) {
        const countryId = getCountryId(this);
        if (!countryId || isIndia(countryId)) return true;
        return hasTrimmedText(value);
      },
    ),
    pinCode: Yup.string().test(
      TENANT_YUP_TEST_NAMES.PIN_CODE,
      ADDRESS_VALIDATION_MESSAGES.PIN_INVALID,
      function (value) {
        const countryId = getCountryId(this);
        const trimmed = String(value ?? "").trim();
        if (!countryId) return true;
        if (!trimmed) {
          return this.createError({
            message: ADDRESS_VALIDATION_MESSAGES.PIN_REQUIRED,
          });
        }
        if (isIndia(countryId) && !INDIA_POSTAL_CODE_PATTERN.test(trimmed)) {
          return this.createError({
            message: ADDRESS_VALIDATION_MESSAGES.PIN_INVALID,
          });
        }
        return true;
      },
    ),
  };
};

const buildSharedTenantPayload = (values, countryOptions) => {
  const {
    name,
    code,
    logo,
    email,
    profileSeparate,
    gstin,
    taxRegistrationNumber,
    countryId,
    timezoneIds,
    currencyIds,
    ambassadorMail,
    gradeIds,
    address,
    contact,
  } = values;

  return {
    name,
    code,
    logo: toNullableString(logo),
    email,
    profile_separate: profileSeparate,
    gstin: toNullableString(gstin),
    tax_registration_number: toTrimmedOrNull(taxRegistrationNumber),
    country_id: Number(countryId),
    timezone_ids: timezoneIds.map(Number),
    currency_ids: currencyIds.map(Number),
    ambassador_mail: toNullableString(ambassadorMail),
    grade_ids: gradeIds.map(Number),
    address: buildAddressPayload(address, countryOptions, countryId),
    contact: buildContactPayload(
      contact,
      getIsdCodeOptionsForCountry(countryOptions, countryId),
    ),
  };
};

export const buildTenantPayload = (values, countryOptions = []) => ({
  ...buildSharedTenantPayload(values, countryOptions),
  vertical_id: Number(values.verticalId),
  has_single_branch: values.hasSingleBranch,
});

export const buildTenantUpdatePayload = (
  values,
  countryOptions = [],
  tenantId,
) => ({
  id: normalizeUuid(tenantId),
  ...buildSharedTenantPayload(values, countryOptions),
});

export const createTenantSchema = (countryOptions) =>
  Yup.object().shape({
    name: Yup.string().required(TENANT_VALIDATION_MESSAGES.NAME_REQUIRED),
    verticalId: requiredIdSchema(
      TENANT_VALIDATION_MESSAGES.VERTICAL_REQUIRED,
      TENANT_VALIDATION_MESSAGES.VERTICAL_SELECT,
    ),
    code: Yup.string()
      .required(TENANT_VALIDATION_MESSAGES.CODE_REQUIRED)
      .matches(
        TENANT_CODE_PATTERN,
        TENANT_VALIDATION_MESSAGES.CODE_FORMAT,
      ),
    email: Yup.string()
      .email(TENANT_VALIDATION_MESSAGES.EMAIL_INVALID)
      .required(TENANT_VALIDATION_MESSAGES.EMAIL_REQUIRED),
    profileSeparate: Yup.boolean(),
    hasSingleBranch: Yup.boolean(),
    gstin: createGstinSchema(),
    taxRegistrationNumber: createTaxRegistrationNumberSchema(),
    countryId: requiredIdSchema(
      TENANT_VALIDATION_MESSAGES.COUNTRY_REQUIRED,
      TENANT_VALIDATION_MESSAGES.COUNTRY_SELECT,
    ),
    timezoneIds: Yup.array()
      .of(Yup.mixed())
      .min(
        MIN_REQUIRED_SELECTION_COUNT,
        TENANT_VALIDATION_MESSAGES.TIMEZONE_MIN,
      )
      .required(TENANT_VALIDATION_MESSAGES.TIMEZONE_REQUIRED),
    currencyIds: Yup.array()
      .of(Yup.mixed())
      .min(
        MIN_REQUIRED_SELECTION_COUNT,
        TENANT_VALIDATION_MESSAGES.CURRENCY_MIN,
      )
      .required(TENANT_VALIDATION_MESSAGES.CURRENCY_REQUIRED),
    ambassadorMail: createOptionalEmailSchema(),
    gradeIds: Yup.array()
      .of(Yup.number())
      .min(MIN_REQUIRED_SELECTION_COUNT, TENANT_VALIDATION_MESSAGES.GRADE_MIN),
    address: Yup.object().shape(
      createAddressSchema(countryOptions, getFormRootCountryId),
    ),
    contact: createContactSchema(countryOptions, getFormRootCountryId),
  });

export const updateTenantCountrySelection = ({
  setFieldValue,
  setFieldTouched,
  previousCountryId,
  nextCountryId,
  countryOptions = [],
}) => {
  setFieldValue(TENANT_FORM_FIELDS.COUNTRY_ID, nextCountryId, true);
  setFieldTouched(TENANT_FORM_FIELDS.COUNTRY_ID, true, false);
  if (String(nextCountryId) === String(previousCountryId)) return;

  setMultipleFieldValues(setFieldValue, ADDRESS_RESET_ON_COUNTRY_CHANGE_FIELDS);
  setFieldValue(TENANT_FORM_FIELDS.CONTACT_ISD_CODE, "", false);
  setFieldValue(TENANT_FORM_FIELDS.CURRENCY_IDS, [], false);
  setFieldValue(TENANT_FORM_FIELDS.TIMEZONE_IDS, [], false);

  const selectedCountry = findOptionById(countryOptions, nextCountryId);
  if (!selectedCountry) {
    setFieldValue(TENANT_FORM_FIELDS.GSTIN, "", false);
    setFieldValue(TENANT_FORM_FIELDS.TAX_REGISTRATION_NUMBER, "", false);
    return;
  }

  const { isIndia, showTaxRegistrationNumber } =
    getAddressFormFlagsForCountry(selectedCountry);
  if (isIndia) {
    setFieldValue(TENANT_FORM_FIELDS.TAX_REGISTRATION_NUMBER, "", false);
  } else if (showTaxRegistrationNumber) {
    setFieldValue(TENANT_FORM_FIELDS.GSTIN, "", false);
  }
};

export const updateContactIsdCode = ({
  setFieldValue,
  setFieldTouched,
  nextIsdCode,
}) => {
  if (!setFieldValue || !setFieldTouched) return;

  setFieldValue(TENANT_FORM_FIELDS.CONTACT_ISD_CODE, nextIsdCode, true);
  setFieldTouched(TENANT_FORM_FIELDS.CONTACT_ISD_CODE, true, false);
};

export const getTenantCountryId = (tenantDetails) =>
  tenantDetails?.countryId ?? tenantDetails?.country_id ?? "";

const buildLookupValue = (value, isLoading) => ({
  value: value ?? null,
  isLoading,
});

const buildMultiIdLabels = (ids, options, isLoading) => ({
  labels: (Array.isArray(ids) ? ids : [])
    .map((id) => findOptionById(options, id)?.label)
    .filter(Boolean),
  isLoading,
});

const buildAddressFieldLookup = ({
  isIndia,
  isPostalLookupLoading,
  isPostalError,
  savedValue,
  resolvedValue,
  savedId,
  resolvedId,
  includeIds,
}) => {
  if (!isIndia) {
    const lookup = buildLookupValue(savedValue, false);
    return includeIds ? { ...lookup, id: savedId ?? null } : lookup;
  }

  if (isPostalLookupLoading) {
    return includeIds
      ? { value: null, isLoading: true, id: null }
      : { value: null, isLoading: true };
  }

  const lookup = {
    value: (isPostalError ? savedValue : resolvedValue || savedValue) ?? null,
    isLoading: false,
  };
  if (!includeIds) return lookup;
  return {
    ...lookup,
    id: (isPostalError ? savedId : resolvedId ?? savedId) ?? null,
  };
};

const withAddressLookups = (address, lookups, isIndia) => ({
  ...address,
  district: isIndia
    ? (lookups?.district?.value ?? address.district)
    : address.district,
  districtId: isIndia
    ? (lookups?.district?.id ?? address.districtId)
    : address.districtId,
  state: isIndia ? (lookups?.state?.value ?? address.state) : address.state,
  stateId: isIndia
    ? (lookups?.state?.id ?? address.stateId)
    : address.stateId,
});

const mapTenantDetailsToFormValuesWithLookups = (
  tenantDetails,
  lookups,
) => {
  if (!tenantDetails) return null;

  const address = normalizeAddressFields(tenantDetails.address);
  const isIndia = lookups?.isIndia;
  const gradeIds = tenantDetails.gradeIds ?? [];

  return resetIncompatibleTaxFieldsForCountry(
    {
      name: tenantDetails.name ?? "",
      email: tenantDetails.email ?? "",
      code: tenantDetails.code || "",
      logo: tenantDetails.logo || "",
      verticalId: tenantDetails.verticalId ?? tenantDetails.vertical_id ?? "",
      profileSeparate: tenantDetails.profileSeparate ?? false,
      hasSingleBranch: tenantDetails.hasSingleBranch ?? false,
      gstin: tenantDetails.gstin || "",
      taxRegistrationNumber:
        tenantDetails.taxRegistrationNumber ??
        tenantDetails.tax_registration_number ??
        "",
      countryId: getTenantCountryId(tenantDetails),
      timezoneIds: normalizeToIdList(
        tenantDetails.timezoneIds,
        tenantDetails.timezone_ids,
        tenantDetails.timezoneId,
        tenantDetails.timezone_id,
      ),
      currencyIds: normalizeToIdList(
        tenantDetails.currencyIds,
        tenantDetails.currency_ids,
        tenantDetails.currencyId,
        tenantDetails.currency_id,
      ),
      ambassadorMail: tenantDetails.ambassadorMail || "",
      gradeIds,
      lockedGradeIds: gradeIds,
      address: withAddressLookups(address, lookups, isIndia),
      contact: normalizeContactFields(tenantDetails.contact),
    },
    { isIndia },
  );
};

const shouldFetchPostalCodeByCountry = (
  countryId,
  pinCode,
  countryItems,
) => {
  const countryName = findMappingItemById(countryItems, countryId)?.displayName;
  return (
    isIndiaCountry(countryName) &&
    String(pinCode ?? "").trim().length === INDIA_POSTAL_CODE_LENGTH
  );
};

const shouldFetchTenantPostalCode = (tenantDetails, countryItems) =>
  shouldFetchPostalCodeByCountry(
    getTenantCountryId(tenantDetails),
    tenantDetails?.address?.pinCode ?? tenantDetails?.address?.pin_code ?? "",
    countryItems,
  );

const useCountryOptionsState = () => {
  const {
    data: countryList,
    isPending: isCountryListLoading,
    isError: isErrorOnCountryList,
    error: errorMessageOnFetchCountryList,
  } = useFetchCountryList();

  const countryItems = useMemo(
    () => countryList?.items ?? [],
    [countryList?.items],
  );
  const countryOptions = useMemo(
    () => getCountryOptions(countryItems),
    [countryItems],
  );

  return {
    countryItems,
    countryOptions,
    isCountryListLoading,
    isErrorOnCountryList,
    errorMessageOnFetchCountryList,
  };
};

const useCountryDetailOptions = (selectedCountry) =>
  useMemo(
    () => ({
      currencyOptions: mapToSelectOptions(
        selectedCountry?.currencies,
        SELECT_OPTION_FALLBACK_LABEL.CURRENCY,
      ),
      timezoneOptions: mapToSelectOptions(
        selectedCountry?.timezones,
        SELECT_OPTION_FALLBACK_LABEL.TIMEZONE,
      ),
      isdCodeOptions: mapToSelectOptions(
        selectedCountry?.isdCodes,
        SELECT_OPTION_FALLBACK_LABEL.ISD,
      ),
    }),
    [selectedCountry],
  );

const buildPostalAddressLookups = ({
  isIndia,
  isPostalLookupLoading,
  isPostalError,
  address,
  pinCodeDetails,
  includeIds,
}) => {
  const shared = { isIndia, isPostalLookupLoading, isPostalError, includeIds };
  return {
    district: buildAddressFieldLookup({
      ...shared,
      savedValue: address.district,
      resolvedValue: pinCodeDetails?.district?.name,
      savedId: address.districtId,
      resolvedId: pinCodeDetails?.district?.id,
    }),
    state: buildAddressFieldLookup({
      ...shared,
      savedValue: address.state,
      resolvedValue: pinCodeDetails?.state?.name,
      savedId: address.stateId,
      resolvedId: pinCodeDetails?.state?.id,
    }),
  };
};

const usePostalAddressState = ({
  address,
  shouldFetchPostal,
  selectedCountry,
  includeIds = false,
}) => {
  const {
    data: postalResponse,
    isPending: isPostalLoading,
    isFetching: isPostalFetching,
    isError: isPostalError,
  } = useFetchPostalCodeDetails(
    shouldFetchPostal ? String(address.pinCode) : undefined,
  );

  const isIndia = isIndiaCountry(selectedCountry?.label);
  const pinCodeDetails = postalResponse?.data;
  const isPostalLookupLoading =
    shouldFetchPostal && (isPostalLoading || isPostalFetching);

  const postalLookups = useMemo(
    () =>
      buildPostalAddressLookups({
        isIndia,
        isPostalLookupLoading,
        isPostalError,
        address,
        pinCodeDetails,
        includeIds,
      }),
    [
      isIndia,
      isPostalLookupLoading,
      isPostalError,
      address,
      pinCodeDetails,
      includeIds,
    ],
  );

  return {
    isIndia,
    isPostalLookupLoading,
    isPostalError,
    postalLookups,
  };
};

export const useTenantDetailsLookups = (
  tenantDetails,
  { includeIds = false } = {},
) => {
  const {
    countryItems,
    countryOptions,
    isCountryListLoading,
    isErrorOnCountryList,
    errorMessageOnFetchCountryList,
  } = useCountryOptionsState();
  const { data: verticalList, isPending: isVerticalListLoading } =
    useFetchVerticalList();
  const { data: gradeList, isPending: isGradeListLoading } =
    useFetchGradeList();

  const countryId = getTenantCountryId(tenantDetails);
  const selectedCountry = findOptionById(countryOptions, countryId);
  const { currencyOptions, timezoneOptions, isdCodeOptions } =
    useCountryDetailOptions(selectedCountry);

  const address = normalizeAddressFields(tenantDetails?.address);
  const shouldFetchPostal = useMemo(() => {
    if (isCountryListLoading) return false;
    return shouldFetchTenantPostalCode(tenantDetails, countryItems);
  }, [tenantDetails, countryItems, isCountryListLoading]);

  const { isIndia, isPostalLookupLoading, isPostalError, postalLookups } =
    usePostalAddressState({
      address,
      shouldFetchPostal,
      selectedCountry,
      includeIds,
    });

  const lookups = useMemo(() => {
    const gradeIds = tenantDetails?.gradeIds ?? [];
    const verticalId =
      tenantDetails?.verticalId ?? tenantDetails?.vertical_id ?? "";
    const verticalItem = verticalList?.items?.find((item) =>
      idsMatch(item.id, verticalId),
    );
    const isdCode =
      tenantDetails?.contact?.isdCode ?? tenantDetails?.contact?.isd_code ?? "";

    return {
      country: buildLookupValue(
        selectedCountry?.label ??
          findMappingItemById(countryItems, countryId)?.displayName,
        isCountryListLoading,
      ),
      currencies: buildMultiIdLabels(
        normalizeToIdList(
          tenantDetails?.currencyIds,
          tenantDetails?.currency_ids,
        ),
        currencyOptions,
        isCountryListLoading,
      ),
      timezones: buildMultiIdLabels(
        normalizeToIdList(
          tenantDetails?.timezoneIds,
          tenantDetails?.timezone_ids,
        ),
        timezoneOptions,
        isCountryListLoading,
      ),
      isdCode: resolveIsdCodeDisplayLabel(isdCode, isdCodeOptions, isCountryListLoading),
      vertical: buildLookupValue(verticalItem?.name, isVerticalListLoading),
      grades: Object.fromEntries(
        gradeIds.map((id) => {
          const gradeItem = gradeList?.items?.find((item) =>
            idsMatch(item.id, id),
          );
          return [
            id,
            buildLookupValue(
              gradeItem ? formatGradeLabel(gradeItem.name) : null,
              isGradeListLoading,
            ),
          ];
        }),
      ),
      ...postalLookups,
      isIndia,
    };
  }, [
    tenantDetails,
    countryItems,
    countryId,
    selectedCountry,
    currencyOptions,
    timezoneOptions,
    isdCodeOptions,
    verticalList?.items,
    gradeList?.items,
    isCountryListLoading,
    isVerticalListLoading,
    isGradeListLoading,
    isIndia,
    postalLookups,
  ]);

  const initialValues = useMemo(
    () => mapTenantDetailsToFormValuesWithLookups(tenantDetails, lookups),
    [tenantDetails, lookups],
  );

  const isMappingListsLoading =
    isCountryListLoading || isVerticalListLoading || isGradeListLoading;

  return {
    lookups,
    countryOptions,
    initialValues,
    isFormReady: !isMappingListsLoading && !isPostalLookupLoading,
    isMappingListsLoading,
    isCountryListLoading,
    isErrorOnCountryList,
    errorMessageOnFetchCountryList,
    isVerticalListLoading,
    isGradeListLoading,
    isPostalLookupLoading,
    isPostalError,
    shouldFetchPostal,
  };
};

export const useBranchDetailsLookups = (branchDetails, tenantCountryId) => {
  const { countryItems, countryOptions, isCountryListLoading } =
    useCountryOptionsState();
  const selectedCountry = findOptionById(countryOptions, tenantCountryId);
  const { isdCodeOptions } = useCountryDetailOptions(selectedCountry);

  const address = normalizeAddressFields(branchDetails?.address);
  const shouldFetchPostal = useMemo(() => {
    if (isCountryListLoading || !tenantCountryId) return false;
    return shouldFetchPostalCodeByCountry(
      tenantCountryId,
      address.pinCode,
      countryItems,
    );
  }, [tenantCountryId, address.pinCode, countryItems, isCountryListLoading]);

  const { isIndia, isPostalLookupLoading, isPostalError, postalLookups } =
    usePostalAddressState({ address, shouldFetchPostal, selectedCountry });

  const lookups = useMemo(() => {
    const isdCode =
      branchDetails?.contact?.isdCode ?? branchDetails?.contact?.isd_code ?? "";
    return {
      isdCode: resolveIsdCodeDisplayLabel(isdCode, isdCodeOptions, isCountryListLoading),
      ...postalLookups,
      isIndia,
    };
  }, [
    branchDetails,
    isdCodeOptions,
    isCountryListLoading,
    isIndia,
    postalLookups,
  ]);

  return {
    lookups,
    address,
    isCountryListLoading,
    isPostalLookupLoading,
    isPostalError,
    shouldFetchPostal,
  };
};
